/**
 * @biomax/plugin-pathogen-database — versioned pathogen-parameter dataset
 * for the BioMax epidemiology engine.
 *
 * Pure data layer modelled after @biomax/plugin-drug-database:
 *   - JSON pathogen records live in `data/pathogens/`
 *   - Every parameter has mandatory PMID citation(s)
 *   - Three-stage validation + audit gate
 *   - Network access only at curation time, never at simulation runtime
 *
 * Status: v0.1 scaffold. Browser-safe entry; Node fs loader at
 * `src/loader.ts`. `npm run audit:pathogens` enforces the
 * verifier-first invariant.
 *
 * kingdoms: '*' (population-level — applies to any host kingdom).
 */

import type { PluginManifest } from '@biomax/types';

export * from './types.js';
export { validatePathogen, type ValidationResult } from './validate.js';
export {
  reload, getCatalogHash, getLoadFailures,
  __setDataDirectory, __setPathogenRecords,
} from './catalog.js';
export { auditPathogen, summariseAudit, type AuditFinding, type AuditReport, type AuditSeverity } from './audit.js';
export {
  verifyPathogen, formatVerificationReport,
  crossCheckClaim, parseClaimedSurnames, parseClaimedYears,
  type PmidResolution, type ResolutionStatus, type VerificationReport,
  type ClaimCheckVerdict, type ClaimCheckResult,
} from './verifier.js';
export { naiveCfr, NAIVE_CFR_CAVEAT, type NaiveCfrResult } from './severity.js';

import {
  loaderFindPathogen, loaderListPathogens,
} from './catalog.js';
import type { Pathogen } from './types.js';

const PLUGIN_ID = 'pathogen-database';

/** Look up a pathogen by id or any registered alias (case-insensitive). */
export function findPathogen(idOrAlias: string): Pathogen | undefined {
  return loaderFindPathogen(idOrAlias);
}

/** List all loaded pathogens. */
export function listPathogens(): Pathogen[] {
  return loaderListPathogens();
}

export class PathogenNotFoundError extends Error {
  constructor(idOrAlias: string) {
    super(`Pathogen not found: "${idOrAlias}"`);
    this.name = 'PathogenNotFoundError';
  }
}

export function requirePathogen(idOrAlias: string): Pathogen {
  const p = findPathogen(idOrAlias);
  if (!p) throw new PathogenNotFoundError(idOrAlias);
  return p;
}

export const manifest: PluginManifest = {
  id: PLUGIN_ID,
  version: '0.1.0',
  engineCompat: '^0.5.0',
  kingdoms: '*',
  systems: [],
  components: [],
};
